import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { beholder } from '@lojinha/beholder'
import {
  Box,
  Button,
  Text,
  Input,
  Stack,
  SuccessfulFeedback,
  ErrorFeedback,
} from '@lojinha/design-system'
import { testId } from '@lojinha/helpers'
import { useForm } from 'react-hook-form'
import {
  cpfValidator,
  formatHolderDocument,
  formatPhoneNumber,
  personNameValidator,
  phoneNumberValidator,
} from '@lojinha/input-helpers'
import { t } from '../../../dictionary'

export type EditProfileFormData = {
  name: string
  phoneNumber: string
  cpf: string
  email: string
}

const EventFormLabels = {
  name: 'Nome',
  phoneNumber: 'Telefone',
  cpf: 'CPF',
  email: 'Email',
} as any

export type Props = {
  onSubmit: (formData: EditProfileFormData) => void
  showErrorMessage: boolean
  setShowErrorMessage: Dispatch<SetStateAction<boolean>>
  defaultValues: {
    name: string
    phoneNumber: string
    cpf: string
    email: string
  }
  loading: boolean
  showSuccessMessage: boolean
  setShowSuccessMessage: Dispatch<SetStateAction<boolean>>
}

export const Form: FC<Props> = ({
  defaultValues,
  loading,
  showSuccessMessage,
  setShowSuccessMessage,
  showErrorMessage,
  setShowErrorMessage,
  onSubmit,
}) => {
  const {
    errors,
    formState: { isValid, dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<EditProfileFormData>({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues,
  })

  const isDirty = !!Object.keys(dirtyFields).length

  const [editedFields, setEditedFields] = useState<string[]>([])

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const labels = dirtyFields as any
    const isEdited = labels[e.target.name]

    if (isEdited) {
      const field = EventFormLabels[e.target.name]

      beholder.shot('structuredEvent', {
        category: 'my_profile',
        action: 'change_profile_settings',
        label: field,
        property: '',
        value: null,
      })

      setEditedFields([...editedFields, field])
    }
  }

  const onFormSubmit = (values: EditProfileFormData) => {
    onSubmit(values)

    beholder.shot('structuredEvent', {
      category: 'my_profile',
      action: 'save_profile_settings',
      label: '',
      property: Array.from(new Set(editedFields)),
      value: null,
    })
  }

  useEffect(() => {
    if (showSuccessMessage) {
      reset(defaultValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccessMessage])

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      {...testId('edit-profile-form')}
    >
      <Box
        boxShadow={'gray16'}
        py={['x24', 'x64']}
        px={['x16', 'x120']}
        height={['100%', '600px']}
        hasRoundedCorner
        htmlAttrs={{
          style: { background: '#fff' },
          'aria-label': t.personalData,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          height={'100%'}
          htmlAttrs={{
            style: { display: showSuccessMessage ? 'none' : 'flex' },
            ...testId('edit-profile-container'),
          }}
        >
          <Box marginBottom="x32">
            <Text isBrand textSize={['x2']} textTransform="uppercase">
              {t.personalData}
            </Text>
          </Box>
          <Stack
            margin={'x32'}
            htmlAttrs={{
              style: {
                marginBottom: 'auto',
              },
            }}
          >
            <Input
              error={errors.name && t.editProfileValidation.invalidName}
              label={t.fullName}
              ariaLabel={t.fullName}
              name="name"
              autoComplete="name"
              onBlur={handleBlur}
              ref={register({
                required: true,
                validate: {
                  name: value => personNameValidator(value),
                },
              })}
              {...testId('edit-name-input')}
            />
            <Input
              error={errors.phoneNumber && t.editProfileValidation.invalidPhone}
              type="tel"
              label={t.phone}
              ariaLabel={t.phoneAriaLabel}
              name="phoneNumber"
              autoComplete="tel"
              onBlur={handleBlur}
              ref={register({
                required: true,
                validate: value =>
                  !!defaultValues.phoneNumber || phoneNumberValidator(value),
              })}
              onChange={e => {
                e.target.value = formatPhoneNumber(e.target.value)
              }}
              disabled={!!defaultValues.phoneNumber}
              {...testId('edit-phone-input')}
            />
            <Input
              error={errors.cpf && t.editProfileValidation.invalidCpf}
              label={t.cpf}
              ariaLabel={t.cpf}
              name="cpf"
              onBlur={handleBlur}
              ref={register({
                required: true,
                validate: value => !!defaultValues.cpf || cpfValidator(value),
              })}
              onChange={e => {
                e.target.value = formatHolderDocument(e.target.value)
              }}
              disabled={!!defaultValues.cpf}
              {...testId('edit-cpf-input')}
            />

            <Input
              error={errors.email && t.editProfileValidation.invalidEmail}
              disabled
              type="email"
              label={t.email}
              ariaLabel={t.email}
              name="email"
              autoComplete="email"
              onBlur={handleBlur}
              ref={register()}
              {...testId('edit-email-input')}
            />
          </Stack>
          <Box
            marginTop={['x40', 'x56']}
            display={showErrorMessage ? 'none' : 'block'}
          >
            <Stack margin={'x24'}>
              <Button
                type="submit"
                isLoading={loading}
                isDisabled={!isValid || !isDirty}
                ariaLabel={t.saveEditions}
                htmlAttrs={{ style: { width: '100%' } }}
                {...testId('btn-save-editions')}
              >
                {t.saveEditions}
              </Button>
              <Button
                type="button"
                isLink
                ariaLabel={t.cancel}
                size={'large'}
                htmlAttrs={{ style: { width: '100%' } }}
                onClick={() => reset(defaultValues)}
              >
                {t.cancel}
              </Button>
            </Stack>
          </Box>
          <Box marginTop={['x40', 'x56']}>
            <ErrorFeedback
              message={t.errorEditProfile}
              shouldShowComponent={showErrorMessage}
              setShouldShowComponent={setShowErrorMessage}
            />
          </Box>
        </Box>
        <Box display="flex" justifyContent="center">
          <SuccessfulFeedback
            message={t.successEditProfile}
            shouldShowComponent={showSuccessMessage}
            setShouldShowComponent={setShowSuccessMessage}
          />
        </Box>
      </Box>
    </form>
  )
}
